import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { formatLightMinute } from '../Utils/light_profiles';
import { lightOutputVertices } from '../Utils/light_profile_source';

export default function SimpleLightCurve({
  cycle,
  channel,
  enabled,
  sunriseDuration,
  zoneMaster = 100,
  subzoneMaster = 100,
  profilePoints,
  outputCurve,
  title
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const width = mobile ? 480 : 800;
  const { vertices, start, end } = outputCurve ?? lightOutputVertices({
    cycle,
    channel,
    enabled,
    sunriseDuration,
    zoneMaster,
    subzoneMaster,
    profilePoints
  });
  const x = (minute) => 45 + ((minute - start) / (end - start)) * (width - 90);
  const axisMaximum = channel?.maximum > 100 ? 115 : 100;
  const y = (level) => 185 - (level / axisMaximum) * 150;
  const path = vertices
    .map(
      ([minute, level], index) =>
        `${index ? 'L' : 'M'}${x(minute).toFixed(3)},${y(level).toFixed(3)}`
    )
    .join(' ');
  return (
    <Box my={2}>
      <Typography variant="body2">{title ?? t('lighting_simple_curve')}</Typography>
      {title !== t('lighting_configured_output') && (
        <Typography variant="caption">{t('lighting_configured_output')}</Typography>
      )}
      <svg
        viewBox={`0 0 ${width} 230`}
        role="img"
        aria-label={title ?? t('lighting_simple_curve')}
        style={{ width: '100%', maxHeight: 320 }}
      >
        <rect
          x={x(cycle.start)}
          y={30}
          width={x(cycle.end) - x(cycle.start)}
          height={155}
          fill="currentColor"
          opacity={0.06}
        />
        {[cycle.start, cycle.end].map((minute) => (
          <line
            key={minute}
            data-day-boundary={minute}
            x1={x(minute)}
            x2={x(minute)}
            y1={30}
            y2={185}
            stroke="currentColor"
            opacity={0.3}
            strokeDasharray="3 4"
          >
            <title>{formatLightMinute(minute)}</title>
          </line>
        ))}
        {[0, 50, 100, ...(axisMaximum > 100 ? [115] : [])].map((level) => (
          <g key={level}>
            <line
              x1={45}
              x2={width - 45}
              y1={y(level)}
              y2={y(level)}
              stroke="currentColor"
              opacity={0.15}
            />
            <text x={0} y={y(level) + 4} fill="currentColor" fontSize={13}>
              {level}%
            </text>
          </g>
        ))}
        <path d={path} fill="none" stroke={theme.palette.primary.main} strokeWidth={3} />
        {[0, 1, 2, 3, 4].map((tick) => {
          const minute = Math.round(start + ((end - start) * tick) / 4);
          return (
            <text
              key={tick}
              x={x(minute)}
              y={218}
              fill="currentColor"
              textAnchor={tick === 0 ? 'start' : tick === 4 ? 'end' : 'middle'}
              fontSize={13}
            >
              {formatLightMinute(minute)}
            </text>
          );
        })}
      </svg>
    </Box>
  );
}
